import { common } from '@/common';
import i18nConfig from '@/i18n/config.json';
import type { GaHostSource, MessageContent, MessageStatus } from '@/types/chat';

const PARAM_MAX_LEN = 500;

const GA_SOURCE_MEASUREMENT_ID: Record<GaHostSource, string> = {
  IPOS_WEB: 'G-K27WNG8YBZ',
  IPOS_REPORT_H5: 'G-3ZT237P519',
  GPOS_WEB: 'G-D43PV586WP',
  GPOS_REPORT_H5: 'G-RHBTXWEGB6',
  STARFISH_WEB: 'G-FDV08W9DMH',
  STARFISH_REPORT_H5: 'G-RYZKSS9ESR',
};

export type GaEventName =
  | 'magic_ai_click'
  | 'magic_ai_loaded'
  | 'faq_impression'
  | 'faq_click'
  | 'ask_question'
  | 'answer_display'
  | 'feedback';

export type AskQuestionSource = 'input' | 'retry' | 'generate_chart' | 'summarize';

const CLIENT_ID_KEY = 'macaw_ga_cid';
const SESSION_ID_KEY = 'macaw_ga_sid';

let hitIndex = 0;
let missingSourceWarned = false;
const answerDisplayKeys = new Set<string>();

function storageGet(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // 隐私模式可能禁止写入，退回本次内存 id
  }
}

function clientId(): string {
  const existing = storageGet(localStorage, CLIENT_ID_KEY);
  if (existing) return existing;
  const created = `${Math.floor(Math.random() * 1e10)}.${Math.floor(Date.now() / 1000)}`;
  storageSet(localStorage, CLIENT_ID_KEY, created);
  return created;
}

function sessionId(): string {
  const existing = storageGet(sessionStorage, SESSION_ID_KEY);
  if (existing) return existing;
  const created = String(Math.floor(Date.now() / 1000));
  storageSet(sessionStorage, SESSION_ID_KEY, created);
  return created;
}

/** 直接打 collect。宿主页面的 gtag / CSP 会吞掉后插入的 gtag.js，Network 里就看不到事件。 */
function sendCollect(
  measurementId: string,
  event: string,
  params: Record<string, string | number>,
): void {
  const query = new URLSearchParams({
    v: '2',
    tid: measurementId,
    cid: clientId(),
    sid: sessionId(),
    sct: '1',
    seg: '1',
    en: event,
    dl: window.location.href,
    dt: document.title,
    ul: navigator.language.toLowerCase(),
    sr: `${window.screen.width}x${window.screen.height}`,
    _s: String(++hitIndex),
    _p: String(Date.now()),
  });
  Object.entries(params).forEach(([key, value]) => {
    const prefix = typeof value === 'number' ? 'epn' : 'ep';
    query.set(`${prefix}.${key}`, String(value));
  });
  const url = `https://www.google-analytics.com/g/collect?${query.toString()}`;
  // sendBeacon 在 Network 里是 ping，开了 Fetch/XHR 过滤就看不到，而且失败也不报错。
  void fetch(url, { method: 'POST', keepalive: true, mode: 'no-cors' }).catch(() => {});
}

function truncate(value: string): string {
  if (value.length <= PARAM_MAX_LEN) return value;
  return value.slice(0, PARAM_MAX_LEN);
}

function resolveRole(): string {
  const code = common.agentRoleCode ?? '';
  if (code === 'DATA_ANALYST') return 'analyst';
  if (code === 'BUSINESS_ANALYST') return 'businessAnalysis';
  return code;
}

function commonParams(): Record<string, string> {
  return {
    projectCode: i18nConfig.projectCode,
    tenantCode: common.tenantCode ?? '',
    storeId: common.storeId ?? '',
    role: resolveRole(),
  };
}

export function resolveGaMeasurementId(): string | null {
  const source = (common.source ?? '').trim().toUpperCase() as GaHostSource;
  return GA_SOURCE_MEASUREMENT_ID[source] ?? null;
}

export function initGoogleAnalytics(): void {
  // 事件在 emitGa 里直接发送，初始化不再插入 gtag.js。
}

export function emitGa(event: GaEventName, params: Record<string, string | number> = {}): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const measurementId = resolveGaMeasurementId();
  if (!measurementId) {
    if (!missingSourceWarned) {
      missingSourceWarned = true;
      console.warn('[Macaw] GA skipped, unknown source:', common.source ?? '');
    }
    return;
  }

  const payload: Record<string, string | number> = { ...commonParams() };
  Object.entries(params).forEach(([key, value]) => {
    payload[key] = typeof value === 'string' ? truncate(value) : value;
  });
  sendCollect(measurementId, event, payload);
}

function normalizeAnswerStatus(status: MessageStatus): 'running' | 'success' | 'error' | 'cancelled' | null {
  if (status === 'sending' || status === 'running' || status === 'streaming') return 'running';
  if (status === 'success' || status === 'error' || status === 'cancelled') return status;
  return null;
}

export function trackAnswerDisplay(
  messageId: string,
  status: MessageStatus | undefined,
  content?: MessageContent,
): void {
  if (!messageId || !status) return;
  const normalized = normalizeAnswerStatus(status);
  if (!normalized) return;

  const key = `${messageId}:${normalized}`;
  if (answerDisplayKeys.has(key)) return;
  answerDisplayKeys.add(key);

  emitGa('answer_display', {
    status: normalized,
    answer_type: content?.chart ? 'chart' : 'text',
  });
}
